import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InspectionRequest } from '@/services/types/inspection.types';
import { inspectionService } from '@/services/inspection.service';

export function useInspectionActions() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isProviderAcceptModalOpen, setIsProviderAcceptModalOpen] = useState(false);
  const [isProviderRejectModalOpen, setIsProviderRejectModalOpen] = useState(false);
  const [isAssignInspectorModalOpen, setIsAssignInspectorModalOpen] = useState(false);
  const [isInspectorAcceptModalOpen, setIsInspectorAcceptModalOpen] = useState(false);
  const [isInspectorRejectModalOpen, setIsInspectorRejectModalOpen] = useState(false);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);

  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [selectedInspection, setSelectedInspection] = useState<InspectionRequest | undefined>();
  const [selectedInspectionForIssue, setSelectedInspectionForIssue] = useState<string | null>(null);
  const [isLoadingInspection, setIsLoadingInspection] = useState(false);

  const [onRefresh, setOnRefresh] = useState<(() => void) | null>(null);

  // Abre o modal de detalhes automaticamente se inspectionId estiver na URL
  useEffect(() => {
    const inspectionIdFromUrl = searchParams.get('inspectionId');
    if (inspectionIdFromUrl) {
      setSelectedInspectionId(inspectionIdFromUrl);
      setSelectedItemId(inspectionIdFromUrl);
      setIsDetailModalOpen(true);
      // Remove o parâmetro da URL após abrir o modal
      searchParams.delete('inspectionId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchInspectionData = useCallback(async (itemId: string) => {
    setIsLoadingInspection(true);
    try {
      const itemData = await inspectionService.getInspectionItemById(itemId);
      if (itemData && itemData.inspectionRequestId) {
        const requestData = await inspectionService.getInspectionRequestById(itemData.inspectionRequestId);
        setSelectedInspection(requestData);
      }
    } catch {
      // Ignore errors when fetching inspection data
    } finally {
      setIsLoadingInspection(false);
    }
  }, []);

  const handleSchedule = useCallback(async (itemId: string, inspectionId: string) => {
    setSelectedInspectionId(itemId);
    await fetchInspectionData(itemId);
    setIsScheduleModalOpen(true);
  }, [fetchInspectionData]);

  const handleCloseScheduleModal = useCallback(() => {
    setIsScheduleModalOpen(false);
    setSelectedInspectionId(null);
    setSelectedInspection(undefined);
  }, []);

  const handleScheduleSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleAssign = useCallback(async (itemId: string, inspectionId: string) => {
    setSelectedInspectionId(itemId);
    await fetchInspectionData(itemId);
    setIsAssignModalOpen(true);
  }, [fetchInspectionData]);

  const handleCloseAssignModal = useCallback(() => {
    setIsAssignModalOpen(false);
    setSelectedInspectionId(null);
    setSelectedInspection(undefined);
  }, []);

  const handleAssignSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleRowClick = useCallback((itemId: string, inspectionId: string) => {
    setSelectedInspectionId(inspectionId);
    setSelectedItemId(itemId);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedInspectionId(null);
    setSelectedItemId(null);
  }, []);

  const handleProviderAccept = useCallback((assignmentId: string, inspectionId: string) => {
    setSelectedAssignmentId(assignmentId);
    setIsProviderAcceptModalOpen(true);
  }, []);

  const handleProviderReject = useCallback((assignmentId: string, inspectionId: string) => {
    setSelectedAssignmentId(assignmentId);
    setIsProviderRejectModalOpen(true);
  }, []);

  const handleCloseProviderAcceptModal = useCallback(() => {
    setIsProviderAcceptModalOpen(false);
    setSelectedAssignmentId(null);
  }, []);

  const handleCloseProviderRejectModal = useCallback(() => {
    setIsProviderRejectModalOpen(false);
    setSelectedAssignmentId(null);
  }, []);

  const handleProviderResponseSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleAssignInspector = useCallback((assignmentId: string, inspectionId: string) => {
    setSelectedAssignmentId(assignmentId);
    setIsAssignInspectorModalOpen(true);
  }, []);

  const handleCloseAssignInspectorModal = useCallback(() => {
    setIsAssignInspectorModalOpen(false);
    setSelectedAssignmentId(null);
    setSelectedProviderId(null);
  }, []);

  const handleAssignInspectorSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleInspectorAccept = useCallback((assignmentId: string, inspectionId: string) => {
    setSelectedAssignmentId(assignmentId);
    setIsInspectorAcceptModalOpen(true);
  }, []);

  const handleInspectorReject = useCallback((assignmentId: string, inspectionId: string) => {
    setSelectedAssignmentId(assignmentId);
    setIsInspectorRejectModalOpen(true);
  }, []);

  const handleCloseInspectorAcceptModal = useCallback(() => {
    setIsInspectorAcceptModalOpen(false);
    setSelectedAssignmentId(null);
  }, []);

  const handleCloseInspectorRejectModal = useCallback(() => {
    setIsInspectorRejectModalOpen(false);
    setSelectedAssignmentId(null);
  }, []);

  const handleInspectorResponseSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleCreateIssue = useCallback((itemId: string) => {
    setSelectedInspectionForIssue(itemId);
    setIsCreateIssueModalOpen(true);
  }, []);

  const handleCloseCreateIssueModal = useCallback(() => {
    setIsCreateIssueModalOpen(false);
    setSelectedInspectionForIssue(null);
  }, []);

  const handleCreateIssueSuccess = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  return {
    isScheduleModalOpen,
    isAssignModalOpen,
    isDetailModalOpen,
    isProviderAcceptModalOpen,
    isProviderRejectModalOpen,
    isAssignInspectorModalOpen,
    isInspectorAcceptModalOpen,
    isInspectorRejectModalOpen,
    isCreateIssueModalOpen,
    selectedInspectionId,
    selectedItemId,
    selectedAssignmentId,
    selectedProviderId,
    selectedInspection,
    selectedInspectionForIssue,
    isLoadingInspection,
    setOnRefresh,
    handleSchedule,
    handleAssign,
    handleRowClick,
    handleProviderAccept,
    handleProviderReject,
    handleAssignInspector,
    handleInspectorAccept,
    handleInspectorReject,
    handleCreateIssue,
    handleCloseScheduleModal,
    handleCloseAssignModal,
    handleCloseDetailModal,
    handleCloseProviderAcceptModal,
    handleCloseProviderRejectModal,
    handleCloseAssignInspectorModal,
    handleCloseInspectorAcceptModal,
    handleCloseInspectorRejectModal,
    handleCloseCreateIssueModal,
    handleScheduleSuccess,
    handleAssignSuccess,
    handleProviderResponseSuccess,
    handleAssignInspectorSuccess,
    handleInspectorResponseSuccess,
    handleCreateIssueSuccess,
  };
}
